import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createRecord, useRecords } from '../lib/api'
import { useSession } from '../lib/auth'
import { storage } from '../lib/config'
import { useLocation } from '../lib/router'
import { fmtDay, pct, today, usd } from '../lib/format'
import { bioScore, CRITERIOS_BIO, orientacionScore, pav, rentabilidad, ruidoScore, type PavAdjust, type PavFactor, type RentInputs } from '../lib/finance'
import { Button, Icon, PageHeader, Segmented, Star, useToast } from '../components/ui'

type Tab = 'pav' | 'renta' | 'bio'

export function Calculadoras() {
  const { query } = useLocation()
  const [tab, setTab] = useState<Tab>((query.get('tab') as Tab) || 'renta')
  const [propId, setPropId] = useState(query.get('propiedad') ?? '')
  const props = useRecords('propiedades').data
  const evals = useRecords('evaluaciones').data
  const prop = props.find(p => p.id === propId)

  return (
    <div className="page">
      <PageHeader eyebrow="Módulo 3" title="Calculadoras" subtitle="Fórmulas y supuestos a la vista. Cada resultado se puede guardar en la ficha de la propiedad."
        actions={
          <div className="field inline-field">
            <label htmlFor="calc-prop">Propiedad</label>
            <select id="calc-prop" value={propId} onChange={e => setPropId(e.target.value)}>
              <option value="">Sin propiedad (simulación libre)</option>
              {props.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
        } />
      <Segmented value={tab} onChange={setTab} options={[
        { value: 'renta', label: 'Rentabilidad', icon: 'calc' },
        { value: 'pav', label: 'P.A.V.', icon: 'layers' },
        { value: 'bio', label: 'Confort biofílico', icon: 'sun' },
      ]} />
      <div className="calc-wrap">
        {tab === 'renta' && <Rentabilidad prop={prop} key={`r${propId}`} />}
        {tab === 'pav' && <Pav prop={prop} key={`p${propId}`} />}
        {tab === 'bio' && <Bio prop={prop} key={`b${propId}`} />}
      </div>
      {prop && (
        <section className="panel">
          <header className="panel-head"><h2>Evaluaciones guardadas de {prop.nombre}</h2></header>
          <ul className="ctx-list pad">
            {evals.filter(e => e.propiedad === prop.id).map(e => <li key={e.id}><span>{fmtDay(e.fecha)}</span> <strong>{e.calculadora}</strong> · {e.resultado} {e.socia && `· ${e.socia}`}</li>)}
            {!evals.some(e => e.propiedad === prop.id) && <li className="muted">Todavía no hay evaluaciones guardadas.</li>}
          </ul>
        </section>
      )}
    </div>
  )
}

/* ------------------------------- Helpers ------------------------------- */

function Num({ id, label, value, onChange, suffix, prefix, step = 'any', help }: { id: string; label: string; value: number; onChange: (n: number) => void; suffix?: string; prefix?: string; step?: string; help?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className={prefix ? 'with-prefix' : suffix ? 'with-suffix' : ''}>
        {prefix && <span className="prefix">{prefix}</span>}
        <input id={id} type="number" inputMode="decimal" step={step} value={Number.isFinite(value) ? value : ''} onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
        {suffix && <span className="suffix">{suffix}</span>}
      </div>
      {help && <small className="field-help">{help}</small>}
    </div>
  )
}

function SaveEval({ prop, calculadora, resultado, valor, entradas }: { prop?: any; calculadora: string; resultado: string; valor: number; entradas: any }) {
  const { name } = useSession()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <div className="save-eval">
      <div><span className="ctx-label">Resultado a guardar</span><strong>{resultado}</strong></div>
      <Button variant="primary" icon="check" busy={busy} disabled={!prop} onClick={async () => {
        setBusy(true)
        try {
          await createRecord('evaluaciones', { titulo: `${calculadora} · ${prop.nombre}`, calculadora, propiedad: prop.id, resultado, puntaje: Math.round(valor * 100) / 100, entradas, socia: name, fecha: today() })
          toast('Evaluación guardada en Notion')
        } catch (e: any) { toast(e.message, 'error') } finally { setBusy(false) }
      }}>{prop ? `Guardar en ${prop.nombre}` : 'Elegí una propiedad para guardar'}</Button>
    </div>
  )
}

const Formula = ({ children }: { children: ReactNode }) => <div className="formula">{children}</div>

/* ----------------------------- Rentabilidad ----------------------------- */

function Rentabilidad({ prop }: { prop?: any }) {
  const [i, setI] = useState<RentInputs>({
    precio: prop?.precio || 98000, costosIniciales: Math.round((prop?.precio || 98000) * 0.07), alquiler: 520, vacancia: 8,
    mantenimiento: 900, impuestos: 450, administracion: 0, crecimiento: 3, revalorizacion: 2.5, costoVenta: 4,
    horizonte: 10, descuento: 8, salida: 'venta',
  })
  const set = (k: keyof RentInputs) => (v: any) => setI(x => ({ ...x, [k]: v }))
  const r = useMemo(() => rentabilidad(i), [i])
  const ok = (n: number) => (n >= 0 ? 'pos' : 'neg')
  return (
    <div className="calc">
      <section className="calc-inputs panel">
        <h3>Supuestos</h3>
        <div className="form-grid">
          <Num id="r-precio" label="Precio de compra" prefix="USD" value={i.precio} onChange={set('precio')} />
          <Num id="r-costos" label="Costos iniciales" prefix="USD" value={i.costosIniciales} onChange={set('costosIniciales')} help="Escribanía, comisión, impuestos de compra, reformas." />
          <Num id="r-alq" label="Alquiler mensual" prefix="USD" value={i.alquiler} onChange={set('alquiler')} />
          <Num id="r-vac" label="Vacancia" suffix="% del año" value={i.vacancia} onChange={set('vacancia')} />
          <Num id="r-mant" label="Gastos comunes y mantenimiento" prefix="USD" value={i.mantenimiento} onChange={set('mantenimiento')} help="Por año." />
          <Num id="r-imp" label="Impuestos y tributos" prefix="USD" value={i.impuestos} onChange={set('impuestos')} help="Por año: contribución, primaria, IRPF si corresponde." />
          <Num id="r-adm" label="Administración" suffix="% del alquiler" value={i.administracion} onChange={set('administracion')} />
          <Num id="r-g" label="Aumento anual de alquiler y gastos" suffix="%" value={i.crecimiento} onChange={set('crecimiento')} />
          <Num id="r-n" label="Horizonte de inversión" suffix="años" step="1" value={i.horizonte} onChange={set('horizonte')} />
          <Num id="r-r" label="Tasa de descuento" suffix="% anual" value={i.descuento} onChange={set('descuento')} help="El retorno mínimo que pide el inversor." />
        </div>
        <div className="field">
          <label>Escenario de salida</label>
          <Segmented value={i.salida} onChange={set('salida')} options={[{ value: 'venta', label: 'Venta al final del horizonte' }, { value: 'gordon', label: 'Mantener a largo plazo (Gordon)' }]} />
        </div>
        {i.salida === 'venta' ? (
          <div className="form-grid">
            <Num id="r-h" label="Revalorización anual" suffix="%" value={i.revalorizacion} onChange={set('revalorizacion')} />
            <Num id="r-cv" label="Costos de venta" suffix="% del precio" value={i.costoVenta} onChange={set('costoVenta')} />
          </div>
        ) : (
          <p className={`hint ${r.gordonValido ? '' : 'hint-warn'}`}>
            {r.gordonValido
              ? 'El modelo de Gordon valúa la propiedad como una renta que crece para siempre a una tasa constante. Usalo solo si el inversor piensa mantenerla y la renta es estable.'
              : 'El modelo de Gordon no aplica: la tasa de descuento tiene que ser mayor que el aumento anual. Usá el escenario de venta.'}
          </p>
        )}
      </section>

      <section className="calc-results">
        <div className="kpis">
          <Kpi label="Ingreso bruto anual" value={usd(r.ingresoBruto)} />
          <Kpi label="Alquiler neto anual (NOI)" value={usd(r.noi)} />
          <Kpi label="Rentabilidad bruta" value={pct(r.rentaBruta)} />
          <Kpi label="Rentabilidad neta" value={pct(r.rentaNeta)} highlight />
          <Kpi label="CAP rate" value={pct(r.capRate)} />
          <Kpi label="VAN" value={usd(r.van)} tone={ok(r.van)} />
          <Kpi label="TIR" value={r.tir == null ? 'No converge' : pct(r.tir)} tone={r.tir != null && r.tir * 100 >= i.descuento ? 'pos' : 'neg'} highlight />
          <Kpi label={i.salida === 'gordon' ? 'Valor terminal (Gordon)' : 'Venta neta al final'} value={usd(r.valorSalida)} />
        </div>
        <p className="verdict">
          <Star size={13} />
          {r.tir == null ? 'Con estos supuestos la inversión no recupera el capital.'
            : r.van >= 0 ? `Con estos supuestos la inversión rinde ${pct(r.tir)} anual, por encima del ${i.descuento} % pedido.`
            : `Rinde ${pct(r.tir)} anual, por debajo del ${i.descuento} % pedido.`}
        </p>
        <div className="table-wrap">
          <table className="table compact">
            <thead><tr><th>Año</th><th className="num">Alquiler neto</th><th className="num">Salida</th><th className="num">Flujo</th><th className="num">Flujo descontado</th></tr></thead>
            <tbody>{r.flujos.map(f => (
              <tr key={f.anio}><td>{f.anio}</td><td className="num">{f.anio ? usd(f.noi) : '—'}</td><td className="num">{f.salida ? usd(f.salida) : '—'}</td><td className={`num ${f.flujo < 0 ? 'neg' : ''}`}>{usd(f.flujo)}</td><td className="num">{usd(f.descontado)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <details className="formulas" open>
          <summary>Fórmulas utilizadas</summary>
          <Formula>Ingreso efectivo = alquiler × 12 × (1 − vacancia)</Formula>
          <Formula>Alquiler neto (NOI) = ingreso efectivo × (1 − administración) − gastos − impuestos</Formula>
          <Formula>Rentabilidad bruta = alquiler anual ÷ precio · Rentabilidad neta = NOI ÷ (precio + costos iniciales)</Formula>
          <Formula>CAP rate = NOI ÷ precio de compra</Formula>
          <Formula>VAN = Σ flujo<sub>t</sub> ÷ (1 + r)<sup>t</sup>, con flujo<sub>0</sub> = −(precio + costos)</Formula>
          <Formula>TIR = tasa que hace VAN = 0</Formula>
          {i.salida === 'gordon' ? <Formula>Valor terminal (Gordon) = NOI<sub>N+1</sub> ÷ (r − g), solo si r &gt; g</Formula> : <Formula>Venta neta = precio × (1 + revalorización)<sup>N</sup> × (1 − costos de venta)</Formula>}
          <p className="hint">Los valores son estimaciones según los supuestos cargados; no son una promesa de rentabilidad.</p>
        </details>
        <SaveEval prop={prop} calculadora="Rentabilidad" valor={r.rentaNeta * 100}
          resultado={`Neta ${pct(r.rentaNeta)} · CAP ${pct(r.capRate)} · TIR ${r.tir == null ? '—' : pct(r.tir)} · VAN ${usd(r.van)}`} entradas={i} />
      </section>
    </div>
  )
}

const Kpi = ({ label, value, highlight, tone }: { label: string; value: string; highlight?: boolean; tone?: string }) => (
  <div className={`kpi ${highlight ? 'kpi-hl' : ''} ${tone ? `kpi-${tone}` : ''}`}><span>{label}</span><strong>{value}</strong></div>
)

/* -------------------------------- P.A.V. -------------------------------- */

const PAV_DEFAULT: PavFactor[] = [
  { key: 'ubicacion', label: 'Ubicación', max: 15, score: 3 },
  { key: 'arquitectura', label: 'Calidad arquitectónica', max: 10, score: 3 },
  { key: 'terminaciones', label: 'Terminaciones', max: 8, score: 3 },
  { key: 'diferenciales', label: 'Características diferenciales', max: 10, score: 3 },
  { key: 'marca', label: 'Valor de marca o autor', max: 12, score: 3 },
]
const PAV_KEY = 'revela.pav.factores'

function Pav({ prop }: { prop?: any }) {
  const [m2, setM2] = useState<number>(prop?.m2 || 200)
  const [usdM2, setUsdM2] = useState<number>(prop?.precio && prop?.m2 ? Math.round(prop.precio / prop.m2) : 1100)
  const [useM2, setUseM2] = useState(true)
  const [vrDirect, setVrDirect] = useState<number>(prop?.precio || 220000)
  const [factors, setFactors] = useState<PavFactor[]>(() => {
    try { const saved = JSON.parse(storage.get(PAV_KEY) || 'null'); if (saved) return saved.map((f: PavFactor) => ({ ...f, score: 3 })) } catch { /* sin datos */ }
    return PAV_DEFAULT
  })
  const [adjusts, setAdjusts] = useState<PavAdjust[]>([])
  useEffect(() => { storage.set(PAV_KEY, JSON.stringify(factors.map(f => ({ key: f.key, label: f.label, max: f.max, score: 3 })))) }, [factors.map(f => f.max + f.label).join()])
  const vr = useM2 ? m2 * usdM2 : vrDirect
  const res = pav(vr, factors, adjusts)
  const setF = (k: string, patch: Partial<PavFactor>) => setFactors(fs => fs.map(f => (f.key === k ? { ...f, ...patch } : f)))
  const SCORE = ['', 'Muy por debajo', 'Por debajo', 'En línea con el mercado', 'Superior', 'Excepcional']

  return (
    <div className="calc">
      <section className="calc-inputs panel">
        <h3>Valor de referencia</h3>
        <Segmented value={useM2 ? 'm2' : 'directo'} onChange={v => setUseM2(v === 'm2')} options={[{ value: 'm2', label: 'Por m² comparable' }, { value: 'directo', label: 'Valor directo' }]} />
        {useM2 ? (
          <div className="form-grid">
            <Num id="pav-m2" label="Superficie" suffix="m²" value={m2} onChange={setM2} />
            <Num id="pav-usdm2" label="Valor m² de comparables" prefix="USD" value={usdM2} onChange={setUsdM2} help="Promedio de ventas comparables de la zona." />
          </div>
        ) : <Num id="pav-vr" label="Valor de referencia" prefix="USD" value={vrDirect} onChange={setVrDirect} />}

        <h3>Factores de prestigio</h3>
        <p className="hint">Puntuá de 1 a 5 (3 = en línea con el mercado). El máximo define cuánto puede mover el valor cada factor; se puede cambiar y queda recordado en este navegador.</p>
        <div className="pav-factors">
          {factors.map(f => (
            <div key={f.key} className="pav-row">
              <div className="pav-name"><strong>{f.label}</strong><span>{SCORE[f.score]}</span></div>
              <input type="range" min={1} max={5} step={1} value={f.score} onChange={e => setF(f.key, { score: Number(e.target.value) })} aria-label={`Puntaje de ${f.label}`} />
              <div className="pav-max"><span>±</span><input type="number" value={f.max} onChange={e => setF(f.key, { max: Number(e.target.value) })} aria-label={`Máximo de ${f.label} en %`} /><span>%</span></div>
            </div>
          ))}
        </div>

        <h3>Ajustes aplicados</h3>
        {adjusts.map((a, idx) => (
          <div key={idx} className="adj-row">
            <input placeholder="Descripción (ej.: techo a reparar)" value={a.label} onChange={e => setAdjusts(x => x.map((y, j) => (j === idx ? { ...y, label: e.target.value } : y)))} aria-label="Descripción del ajuste" />
            <select value={a.tipo} onChange={e => setAdjusts(x => x.map((y, j) => (j === idx ? { ...y, tipo: e.target.value as any } : y)))} aria-label="Tipo de ajuste"><option value="pct">%</option><option value="usd">USD</option></select>
            <input type="number" value={a.valor} onChange={e => setAdjusts(x => x.map((y, j) => (j === idx ? { ...y, valor: Number(e.target.value) } : y)))} aria-label="Valor del ajuste" />
            <button type="button" className="icon-btn sm" aria-label="Quitar ajuste" onClick={() => setAdjusts(x => x.filter((_, j) => j !== idx))}><Icon name="x" size={13} /></button>
          </div>
        ))}
        <Button size="sm" variant="quiet" icon="plus" onClick={() => setAdjusts(x => [...x, { label: '', tipo: 'usd', valor: 0 }])}>Agregar ajuste</Button>
      </section>

      <section className="calc-results">
        <div className="pav-hero">
          <span className="ctx-label">Prestige Adjusted Value</span>
          <strong>{usd(res.total)}</strong>
          <span className={res.prima >= 0 ? 'pos' : 'neg'}>{res.prima >= 0 ? '+' : ''}{pct(res.prima)} sobre el valor de referencia ({usd(vr)})</span>
        </div>
        <div className="table-wrap">
          <table className="table compact">
            <thead><tr><th>Factor</th><th className="num">Puntaje</th><th className="num">Máximo</th><th className="num">Ajuste</th><th className="num">USD</th></tr></thead>
            <tbody>
              {res.rows.map(f => <tr key={f.key}><td>{f.label}</td><td className="num">{f.score} / 5</td><td className="num">±{f.max} %</td><td className="num">{pct(f.ajuste)}</td><td className="num">{usd(vr * f.ajuste)}</td></tr>)}
              {adjusts.map((a, i) => <tr key={`a${i}`}><td>{a.label || 'Ajuste'}</td><td /><td /><td className="num">{a.tipo === 'pct' ? `${a.valor} %` : '—'}</td><td className="num">{usd(a.tipo === 'pct' ? (vr * a.valor) / 100 : a.valor)}</td></tr>)}
            </tbody>
          </table>
        </div>
        <details className="formulas" open>
          <summary>Fórmula y supuestos</summary>
          <Formula>Ajuste de cada factor = máximo × (puntaje − 3) ÷ 2</Formula>
          <Formula>PAV = valor de referencia × (1 + Σ ajustes de factores + Σ ajustes %) + Σ ajustes en USD</Formula>
          <p className="hint">Valor de referencia: {useM2 ? `${m2} m² × ${usd(usdM2)} por m²` : 'cargado directamente'}. El PAV es una herramienta de análisis interno para fundamentar un precio; no reemplaza una tasación profesional.</p>
        </details>
        <SaveEval prop={prop} calculadora="P.A.V." valor={res.total} resultado={`${usd(res.total)} (${res.prima >= 0 ? '+' : ''}${pct(res.prima)} sobre referencia)`} entradas={{ valorReferencia: vr, m2: useM2 ? m2 : undefined, usdM2: useM2 ? usdM2 : undefined, factores: factors, ajustes: adjusts }} />
      </section>
    </div>
  )
}

/* --------------------------- Confort biofílico --------------------------- */

function Bio({ prop }: { prop?: any }) {
  const [scores, setScores] = useState<Record<string, number>>(() => ({
    luz: prop?.iluminacion || 3, orientacion: orientacionScore(prop?.orientacion), acustica: ruidoScore(prop?.ruido), ventilacion: 3, circadiano: 3,
  }))
  const { total, label } = bioScore(scores)
  const bajos = CRITERIOS_BIO.filter(c => (scores[c.key] ?? 3) <= 3)
  const R = 54, C = 2 * Math.PI * R
  return (
    <div className="calc">
      <section className="calc-inputs panel">
        <h3>Criterios</h3>
        {prop && <p className="hint">Precargado con los datos de {prop.nombre}: luz {prop.iluminacion ?? '—'}/5, orientación {prop.orientacion || '—'}, ruido {prop.ruido ? `${prop.ruido} dB` : 'sin medir'}.</p>}
        <div className="bio-list">
          {CRITERIOS_BIO.map((c, i) => (
            <div key={c.key} className="bio-row">
              <div className="bio-head"><span className="bio-n">{i + 1}</span><strong>{c.label}</strong><span className="bio-val">{scores[c.key]} / 5</span></div>
              <p>{c.desc}</p>
              <div className="dots" role="radiogroup" aria-label={c.label}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" role="radio" aria-checked={scores[c.key] === n} className={`dot-btn ${scores[c.key] >= n ? 'on' : ''}`} onClick={() => setScores(s => ({ ...s, [c.key]: n }))}>{n}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="calc-results">
        <div className="bio-score">
          <svg viewBox="0 0 140 140" width="150" height="150" role="img" aria-label={`Puntaje ${total} de 100`}>
            <circle cx="70" cy="70" r={R} fill="none" stroke="var(--line)" strokeWidth="10" />
            <circle cx="70" cy="70" r={R} fill="none" stroke="var(--olive)" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${(C * total) / 100} ${C}`} transform="rotate(-90 70 70)" />
            <text x="70" y="72" textAnchor="middle" className="bio-num">{total}</text>
            <text x="70" y="94" textAnchor="middle" className="bio-of">de 100</text>
          </svg>
          <div><span className="ctx-label">Confort biofílico</span><strong className="bio-label">{label}</strong>
            <p className="muted">Promedio de los cinco criterios, cada uno con el mismo peso.</p></div>
        </div>
        <div className="bio-bars">
          {CRITERIOS_BIO.map(c => <div key={c.key} className="bio-bar"><span>{c.label}</span><div><span style={{ width: `${(scores[c.key] / 5) * 100}%` }} /></div></div>)}
        </div>
        <div className="panel pad">
          <h3>Recomendaciones</h3>
          {bajos.length ? <ul className="recs">{bajos.map(c => <li key={c.key}><strong>{c.label}.</strong> {c.rec}</li>)}</ul>
            : <p>La propiedad destaca en los cinco criterios. Usalo como argumento en la presentación: luz, silencio y conexión con el exterior.</p>}
        </div>
        <SaveEval prop={prop} calculadora="Confort biofílico" valor={total} resultado={`${total} / 100 · ${label}`} entradas={scores} />
      </section>
    </div>
  )
}
